import {
    AppShell,
    Box,
    NavLink as MantineNavLink,
    ScrollArea,
    Stack,
    Text,
    em,
    rem,
} from '@mantine/core';
import {
    IconChevronRight,
    IconDashboard,
    IconDeviceMobile,
    IconDeviceSdCard,
    IconFolder,
    IconGauge,
    IconLogout,
    IconMail,
    IconMessage,
    IconPlus,
    IconServer2,
    IconSettings,
    IconTools,
    IconTopologyFull,
    IconUser,
} from '@tabler/icons-react';
import { hasAllRoles, hasAnyRoles, hasRouteAccess } from '../../../utils/roles';

import Cookies from 'js-cookie';
import { NavbarLink } from './NavbarLink';
import { NavbarParentItem } from './NavbarParentItem';
import classes from './Navbar.module.css';
import { useAuthStore } from '../../../store';
import { useMemo } from 'react';

const TOP_LINKS = [
    {
        icon: IconDashboard,
        label: 'Dashboard',
        to: '/dashboard',
        routeAccess: {
            roles: ['DASHBOARD'],
            requireAll: false,
        },
    },
    {
        icon: IconServer2,
        label: 'Campaigns',
        to: '/campaigns',
        pattern: '/campaigns/*',
        subItems: [
            {
                label: 'Message Groups',
                to: '/campaigns/messages',
                pattern: '/campaigns/messages/:subview?',
                routeAccess: { roles: ['CAMPAIGNS'] },
            },
            {
                label: 'Media Groups',
                to: '/campaigns/media',
                pattern: '/campaigns/media/:subview?',
                routeAccess: { roles: ['CAMPAIGNS'] },
            },
            {
                label: 'Link Groups',
                to: '/campaigns/links',
                pattern: '/campaigns/links/:subview?',
                routeAccess: { roles: ['CAMPAIGNS'] },
            },
            {
                label: 'Chat Handlers',
                to: '/campaigns/handlers',
                pattern: '/campaigns/handlers/:id?',
                routeAccess: { roles: ['SMS'] },
            },
            {
                label: 'Chat Scripts',
                to: '/campaigns/scripts',
                pattern: '/campaigns/scripts/:id?',
                routeAccess: { roles: ['SMS'] },
            },
            {
                label: 'Chat Bots',
                to: '/campaigns/bots',
                pattern: '/campaigns/bots/:id?',
                routeAccess: { roles: ['SMS_BOTS'] },
            },
            {
                label: 'Sessions',
                to: '/campaigns/sessions',
                routeAccess: { roles: ['CAMPAIGNS'] },
            },
        ],
    },

    {
        icon: IconMessage,
        label: 'Engagement',
        to: '/engagement',
        pattern: '/engagement/*',
        subItems: [
            {
                label: 'SMS Conversations',
                to: '/engagement/sms/conversations',
                pattern: '/engagement/sms/conversations/:id?',
                routeAccess: { roles: ['SMS'] },
            },
            {
                label: 'SMS Messages',
                to: '/engagement/sms/messages',
                pattern: '/engagement/sms/messages/:id?',
                routeAccess: { roles: ['SMS'] },
            },
            {
                label: 'Calls',
                to: '/engagement/calls',
                pattern: '/engagement/calls/:id?',
                routeAccess: { roles: ['VOICE'] },
            },
            {
                label: 'Email',
                to: '/engagement/email',
                pattern: '/engagement/email/:id?',
                routeAccess: { roles: ['EMAIL'] },
            },
        ],
    },

    /*{
        icon: IconMail,
        label: 'Email',
        to: '/email',
        pattern: '/email/*',
        subItems: [
            {
                label: 'Chat Scripts',
                to: '/chat/scripts',
                pattern: '/chat/scripts/:subview?',
                routeAccess: { roles: ['CHAT'] },
            },
            {
                label: 'Chat Bots',
                to: '/chat/bots',
                pattern: '/chat/bots/:subview?',
                routeAccess: { roles: ['CHAT'] },
            },
            {
                label: 'Conversations',
                to: '/chat/conversations',
                pattern: '/chat/conversations/:subview?',
                routeAccess: { roles: ['CHAT'] },
            },
            {
                label: 'Messages',
                to: '/chat/messages',
                pattern: '/chat/messages/:subview?',
                routeAccess: { roles: ['CHAT'] },
            },
        ],
    },*/

    {
        icon: IconFolder,
        label: 'Manage Data',
        to: '/data',
        pattern: '/data/:id?/:subview?',
        routeAccess: { roles: ['DATA'] },
        subItems: [
            {
                label: 'Data Sets',
                to: '/data',
            },
            {
                label: 'Data Files',
                to: '/data/files',
            },
            {
                label: 'Import Records',
                to: '/data/import',
            },
        ],
    },

    {
        icon: IconTools,
        label: 'Tools',
        to: '/tools',
        pattern: '/tools/:id?/:subview?',
        subItems: [
            {
                label: 'SMS Testing',
                to: '/tools/sms/test',
                routeAccess: { roles: ['SMS_TESTING'] },
            },
        ],
    },

    {
        icon: IconTopologyFull,
        label: 'Resources',
        to: '/resources',
        pattern: '/resources/*',
        subItems: [
            {
                label: 'Agents',
                to: '/resources/agents',
                pattern: '/resources/agents/:subview?',
                routeAccess: { roles: ['AGENTS'] },
            },
            {
                label: 'Routes',
                to: '/resources/routes',
                pattern: '/resources/routes/:subview?',
                routeAccess: { roles: ['ROUTES'] },
            },
            {
                label: 'Shortlinks',
                to: '/resources/shortlinks',
                pattern: '/resources/shortlinks/:subview?',
                routeAccess: { roles: ['SHORT_LINKS'] },
            },
            {
                label: 'SIM Cards',
                to: '/resources/simcards',
                pattern: '/resources/simcards/:subview?',
                routeAccess: { roles: ['SIM_CARDS'] },
            },
            {
                label: 'Mobile Devices',
                to: '/resources/devices',
                pattern: '/resources/devices/:subview?',
                routeAccess: { roles: ['MOBILE_DEVICES'] },
            },
            {
                label: 'Carriers',
                to: '/resources/carriers',
                pattern: '/resources/carriers/:subview?',
                routeAccess: { roles: ['CARRIERS'] },
            },
            {
                label: 'APNs',
                to: '/resources/apns',
                pattern: '/resources/apns/:subview?',
                routeAccess: { roles: ['APNS'] },
            },
        ],
    },

    {
        icon: IconUser,
        label: 'Admin',
        to: '/admin/users',
        pattern: '/admin/*',
        routeAccess: { roles: ['SUPER_ADMIN'] },
        subItems: [
            {
                label: 'Users',
                to: '/admin/users',
                routeAccess: { roles: ['SUPER_ADMIN'] },
            },
            {
                label: 'System Events',
                to: '/admin/system/events',
                routeAccess: { roles: ['SUPER_ADMIN'] },
            },
        ],
    },
];

const BOTTOM_LINKS = [
    {
        icon: IconSettings,
        label: 'Settings',
        to: '/settings',
        pattern: '/settings',
    },

    {
        icon: IconLogout,
        label: 'Logout',
        to: '/logout',
        pattern: '/logout',
    },
];

const Navbar = ({ test }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const logout = useAuthStore((state) => state.logout);

    const topLinks = useMemo(
        () =>
            TOP_LINKS.filter((x) =>
                hasRouteAccess(userProfile, x.routeAccess)
            ).map((link, index) => (
                <NavbarParentItem
                    {...link}
                    key={link.to}
                />
            )),
        [TOP_LINKS, userProfile]
    );

    const bottomLinks = useMemo(
        () =>
            BOTTOM_LINKS.filter((x) =>
                hasRouteAccess(userProfile, x.routeAccess)
            ).map((link, index) => (
                <NavbarParentItem
                    {...link}
                    key={link.to}
                />
            )),
        [BOTTOM_LINKS, userProfile]
    );

    const handleLogout = () => {
        logout();
    };

    return (
        <AppShell.Section
            w={rem(200)}
            h={'100%'}
            p={0}>
            <Stack
                justify={'space-between'}
                h={'100%'}>
                <Box>
                    <Stack
                        justify='center'
                        gap={0}>
                        {topLinks}
                    </Stack>
                </Box>

                <Box>
                    <Stack
                        justify='center'
                        gap={0}>
                        {bottomLinks}
                    </Stack>
                </Box>
            </Stack>
        </AppShell.Section>
    );
};
export default Navbar;
